/** @type {import('tailwindcss').Config} */

import defaultTheme from "tailwindcss/defaultTheme"
import formsPlugin from "@tailwindcss/forms"
import flowBitePlugin from "flowbite/plugin"

export default {
    content: ["./assets/**/*.{vue,ts,js,html,css,scss}", "./templates/**/*.html.twig"],
    safelist: [
        {
            pattern: /col-span-+/,
        },
    ],
    theme: {
        container: {
            center: true,
            padding: {
                DEFAULT: "1rem",
                sm: "1.5rem",
                md: "1.5rem",
                lg: "4.5rem",
                xl: "4.5rem",
                "2xl": "4.5rem",
            },
        },
        screens: {
            sm: "576px",
            md: "768px",
            lg: "992px",
            xl: "1200px",
            "2xl": "1400px",
        },
        fontFamily: {
            sans: ["Lato", ...defaultTheme.fontFamily.sans],
        },
        fontSize: {
            micro: "10px",
            xs: "12px",
            sm: "14px",
            base: "16px",
            md: "16px",
            lg: "20px",
            xl: "24px",
            "2xl": "28px",
            "3xl": "32px",
            "4xl": "40px",
            "5xl": "48px",
            "6xl": "56px",
            "7xl": "64px",
            "8xl": "80px",
        },
        fontWeight: {
            light: 300,
            regular: 400,
            bold: 700,
            bolder: 800,
            extrabold: 900,
        },
        extend: {
            boxShadow: {
                floating:
                    "0px 16px 32px 0px rgba(0, 0, 0, 0.02), 0px 2px 16px 0px rgba(0, 0, 0, 0.08), 0px 4px 8px 0px rgba(0, 0, 0, 0.06), 0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 1px 2px 0px rgba(0, 0, 0, 0.10)",
            },
            colors: {
                "gutex-rot": {
                    DEFAULT: "#D82F13",
                    110: "#B3250E",
                },
                tanne: {
                    DEFAULT: "#1B3C35",
                    100: "#1B3C35",
                    90: "#325049",
                    80: "#49635D",
                    70: "#5F7772",
                    60: "#768A86",
                    50: "#8D9E9A",
                    40: "#A4B1AE",
                    30: "#BBC5C2",
                    20: "#D1D8D7",
                    10: "#E8ECEB",
                },
                anthrazit: {
                    DEFAULT: "#423D3B",
                    100: "#423D3B",
                    90: "#55504F",
                    80: "#686462",
                    70: "#7B7776",
                    60: "#8E8B89",
                    50: "#A19E9D",
                    40: "#B3B1B1",
                    30: "#C6C5C4",
                    20: "#D9D8D8",
                    10: "#ECECEB",
                },
                sand: {
                    DEFAULT: "#D3CBC1",
                    100: "#D3CBC1",
                    90: "#D7D0C7",
                    80: "#DCD5CD",
                    70: "#E0DBD4",
                    60: "#E5E0DA",
                    50: "#E9E5E0",
                    40: "#EDEAE6",
                    30: "#F2EFEC",
                    20: "#F6F5F3",
                    10: "#FBFAF9",
                },
            },
            backgroundImage: {
                "gradient-primary": "linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%)",
                grain: "url(\"data:image/svg+xml,%3C!-- svg: first layer --%3E%3Csvg viewBox='0 0 190 190' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='4' numOctaves='6' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
                "chevron-down":
                    "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='black' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='none' stroke-linecap='round' stroke-linejoin='round' fill-rule='evenodd' clip-rule='evenodd' d='M12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071Z'/%3E%3C/svg%3E%0A\")",
                "chevron-up":
                    "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='black' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='none' stroke-linecap='round' stroke-linejoin='round' fill-rule='evenodd' clip-rule='evenodd' d='M11.2929 8.29289C11.6834 7.90237 12.3166 7.90237 12.7071 8.29289L18.7071 14.2929C19.0976 14.6834 19.0976 15.3166 18.7071 15.7071C18.3166 16.0976 17.6834 16.0976 17.2929 15.7071L12 10.4142L6.70711 15.7071C6.31658 16.0976 5.68342 16.0976 5.29289 15.7071C4.90237 15.3166 4.90237 14.6834 5.29289 14.2929L11.2929 8.29289Z'/%3E%3C/svg%3E\")",
                plus: "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='black' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='none' stroke-linecap='round' stroke-linejoin='round' fill-rule='evenodd' clip-rule='evenodd' d='M12 4C12.5523 4 13 4.44772 13 5V11H19C19.5523 11 20 11.4477 20 12C20 12.5523 19.5523 13 19 13H13V19C13 19.5523 12.5523 20 12 20C11.4477 20 11 19.5523 11 19V13H5C4.44772 13 4 12.5523 4 12C4 11.4477 4.44772 11 5 11H11V5C11 4.44772 11.4477 4 12 4Z'/%3E%3C/svg%3E\")",
                minus: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-minus'%3E%3Cline x1='5' y1='12' x2='19' y2='12'%3E%3C/line%3E%3C/svg%3E\")",
                "error-x":
                    "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23B3250E' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 6L6 18M6 6L18 18' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A\")",
                mail: "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='black' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='none' fill-rule='evenodd' clip-rule='evenodd' stroke-linecap='round' stroke-linejoin='round' d='M4.14715 6.32109L12 11.6654L19.8528 6.32109C19.7117 6.13224 19.4791 6 19.2 6H4.8C4.52088 6 4.28831 6.13224 4.14715 6.32109ZM20 8.64017L12.5626 13.7017C12.2231 13.9328 11.7769 13.9328 11.4374 13.7017L4 8.64017V17.25C4 17.6338 4.33554 18 4.8 18H19.2C19.6645 18 20 17.6338 20 17.25V8.64017ZM2 6.75C2 5.20885 3.28446 4 4.8 4H19.2C20.7155 4 22 5.20885 22 6.75V17.25C22 18.7912 20.7155 20 19.2 20H4.8C3.28446 20 2 18.7912 2 17.25V6.75Z'/%3E%3C/svg%3E%0A\")",
                phone: "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='black' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' stroke='none' stroke-linecap='round' stroke-linejoin='round' d='M2 4.38687C2 3.05843 3.07738 2 4.38687 2H7.28757C8.34387 2 9.2708 2.68951 9.57367 3.69909L9.57468 3.70244L10.5631 7.03971C10.813 7.90577 10.562 8.8298 9.90964 9.44874L9.9041 9.454L9.0381 10.2631C9.06865 10.3289 9.10448 10.4018 9.14642 10.4813C9.42591 11.0115 9.98024 11.8476 11.0614 12.9288L11.0712 12.9385C12.1838 14.0512 13.0189 14.6034 13.5368 14.8735C13.6072 14.9102 13.6718 14.9418 13.7305 14.9688L14.5512 14.0903C15.1702 13.4379 16.0942 13.1871 16.9603 13.4369L16.9671 13.4389L20.3009 14.4263C21.3105 14.7292 22 15.6561 22 16.7124V19.6131C22 20.9416 20.9226 22 19.6131 22C9.87978 22 2 14.1097 2 4.38687ZM14.0266 16.1167C13.9307 17.1121 13.9292 17.1119 13.9292 17.1119L13.9276 17.1117L13.9243 17.1114L13.9174 17.1107L13.9021 17.1089C13.8913 17.1076 13.8792 17.106 13.8659 17.104C13.8392 17.1001 13.8076 17.0948 13.771 17.0876C13.6978 17.0731 13.6053 17.0509 13.4941 17.0173C13.2714 16.9498 12.9757 16.8366 12.6119 16.6468C11.8845 16.2674 10.8883 15.5841 9.65697 14.3528L9.64724 14.3431C8.44301 13.1388 7.76182 12.1436 7.37718 11.414C7.18494 11.0492 7.06757 10.7524 6.99624 10.531C6.9606 10.4204 6.93658 10.329 6.92047 10.2579C6.91242 10.2224 6.90636 10.1919 6.90182 10.1667C6.89955 10.1541 6.89766 10.1428 6.89609 10.1328L6.89398 10.1189L6.89309 10.1127L6.89268 10.1098L6.89249 10.1083C6.89249 10.1083 6.8923 10.107 7.88334 9.97341C8.87438 9.83987 6.8923 10.107 6.8923 10.107C6.84904 9.78594 6.96396 9.46385 7.20064 9.24271L8.53506 7.99596C8.64243 7.8928 8.68392 7.74445 8.64215 7.59648L7.65802 4.27378L7.65766 4.27259C7.60857 4.11081 7.46146 4 7.28757 4H4.38687C4.17276 4 4 4.17217 4 4.38687C4 13.0059 10.9851 20 19.6131 20C19.8272 20 20 19.8278 20 19.6131V16.7124C20 16.5385 19.8892 16.3914 19.7274 16.3423L19.7262 16.342L16.406 15.3585L16.4035 15.3578C16.2556 15.3161 16.1073 15.3576 16.0041 15.4649L14.7573 16.7994C14.5453 17.0263 14.2399 17.1418 13.9307 17.1121L14.0266 16.1167Z'/%3E%3C/svg%3E%0A\")",
                "mail-white":
                    "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='none' fill-rule='evenodd' clip-rule='evenodd' stroke-linecap='round' stroke-linejoin='round' d='M4.14715 6.32109L12 11.6654L19.8528 6.32109C19.7117 6.13224 19.4791 6 19.2 6H4.8C4.52088 6 4.28831 6.13224 4.14715 6.32109ZM20 8.64017L12.5626 13.7017C12.2231 13.9328 11.7769 13.9328 11.4374 13.7017L4 8.64017V17.25C4 17.6338 4.33554 18 4.8 18H19.2C19.6645 18 20 17.6338 20 17.25V8.64017ZM2 6.75C2 5.20885 3.28446 4 4.8 4H19.2C20.7155 4 22 5.20885 22 6.75V17.25C22 18.7912 20.7155 20 19.2 20H4.8C3.28446 20 2 18.7912 2 17.25V6.75Z'/%3E%3C/svg%3E%0A\")",
                "phone-white":
                    "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' stroke='none' stroke-linecap='round' stroke-linejoin='round' d='M2 4.38687C2 3.05843 3.07738 2 4.38687 2H7.28757C8.34387 2 9.2708 2.68951 9.57367 3.69909L9.57468 3.70244L10.5631 7.03971C10.813 7.90577 10.562 8.8298 9.90964 9.44874L9.9041 9.454L9.0381 10.2631C9.06865 10.3289 9.10448 10.4018 9.14642 10.4813C9.42591 11.0115 9.98024 11.8476 11.0614 12.9288L11.0712 12.9385C12.1838 14.0512 13.0189 14.6034 13.5368 14.8735C13.6072 14.9102 13.6718 14.9418 13.7305 14.9688L14.5512 14.0903C15.1702 13.4379 16.0942 13.1871 16.9603 13.4369L16.9671 13.4389L20.3009 14.4263C21.3105 14.7292 22 15.6561 22 16.7124V19.6131C22 20.9416 20.9226 22 19.6131 22C9.87978 22 2 14.1097 2 4.38687ZM14.0266 16.1167C13.9307 17.1121 13.9292 17.1119 13.9292 17.1119L13.9276 17.1117L13.9243 17.1114L13.9174 17.1107L13.9021 17.1089C13.8913 17.1076 13.8792 17.106 13.8659 17.104C13.8392 17.1001 13.8076 17.0948 13.771 17.0876C13.6978 17.0731 13.6053 17.0509 13.4941 17.0173C13.2714 16.9498 12.9757 16.8366 12.6119 16.6468C11.8845 16.2674 10.8883 15.5841 9.65697 14.3528L9.64724 14.3431C8.44301 13.1388 7.76182 12.1436 7.37718 11.414C7.18494 11.0492 7.06757 10.7524 6.99624 10.531C6.9606 10.4204 6.93658 10.329 6.92047 10.2579C6.91242 10.2224 6.90636 10.1919 6.90182 10.1667C6.89955 10.1541 6.89766 10.1428 6.89609 10.1328L6.89398 10.1189L6.89309 10.1127L6.89268 10.1098L6.89249 10.1083C6.89249 10.1083 6.8923 10.107 7.88334 9.97341C8.87438 9.83987 6.8923 10.107 6.8923 10.107C6.84904 9.78594 6.96396 9.46385 7.20064 9.24271L8.53506 7.99596C8.64243 7.8928 8.68392 7.74445 8.64215 7.59648L7.65802 4.27378L7.65766 4.27259C7.60857 4.11081 7.46146 4 7.28757 4H4.38687C4.17276 4 4 4.17217 4 4.38687C4 13.0059 10.9851 20 19.6131 20C19.8272 20 20 19.8278 20 19.6131V16.7124C20 16.5385 19.8892 16.3914 19.7274 16.3423L19.7262 16.342L16.406 15.3585L16.4035 15.3578C16.2556 15.3161 16.1073 15.3576 16.0041 15.4649L14.7573 16.7994C14.5453 17.0263 14.2399 17.1418 13.9307 17.1121L14.0266 16.1167Z'/%3E%3C/svg%3E%0A\")",
            },
            height: {
                "screen/90": "90vh",
            },
            lineHeight: {
                120: "120%",
                135: "135%",
                140: "140%",
            },
            spacing: {
                "icon-size-sm": "1rem",
                "icon-size-md": "1.5rem",
                "icon-size-lg": "2rem",
                4.5: "1.125rem",
                18: "4.5rem",
                26.5: "6.625rem",
                30: "7.5rem",
                35: "8.75rem",
                50: "12.5rem",
                54: "13.5rem",
                65: "16.25rem",
                70: "17.5rem",
                75: "18.75rem",
                90: "22.5rem",
                91: "22.75rem",
                102: "25.5rem",
                105: "26.25rem",
                115: "28.75rem",
                133.5: "33.5rem",
            },
            textUnderlineOffset: {
                3: "3px",
            },
            transitionDuration: {
                DEFAULT: "300ms",
            },
        },
    },
    plugins: [formsPlugin, flowBitePlugin],
}
